import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Heart,
  ArrowLeft,
  MoreVertical,
  Edit3,
  Phone,
  History,
  Tag,
  AlertTriangle,
  Cake,
  Mail,
  Plus,
  StickyNote,
  Trash2,
  Pencil,
  Smile,
  User,
  Egg,
  Meh,
  Frown,
  RefreshCw,
  MessageCircle,
} from 'lucide-react';
import { useApp } from '../context';
import { CommunicationMethod } from '../types';
import { notesApi } from '../lib/api';
import { getCommIcon, getCommHref } from '../lib/communicationHelpers';
import { isPreferred } from '../lib/contactFormHelpers';
import { cn, formatDate, formatInteractionType } from '../lib/utils';
import ContactAvatar from './ContactAvatar';
import ImageLightbox from './ImageLightbox';
import AnimatedNumber from './AnimatedNumber';

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    state,
    getContactInteractions,
    updateContact,
    setPendingInteraction,
    removeInteraction,
    deleteContact,
  } = useApp();
  const [showMenu, setShowMenu] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    id: string;
    type: 'note' | 'interaction' | 'contact';
  } | null>(null);

  const contact = state.contacts.find((c) => c.id === id);
  const interactions = getContactInteractions(id || '');
  const isAuth = !state.isGuestMode;

  // ─── Notes state (API-driven for auth, local for guest) ──────────────────
  const [apiNotes, setApiNotes] = useState<{ id: string; text: string; date: string }[]>([]);
  const [failedNoteIds, setFailedNoteIds] = useState<Set<string>>(new Set());
  const [savingNoteIds, setSavingNoteIds] = useState<Set<string>>(new Set());

  // Load notes from API for authenticated users
  useEffect(() => {
    if (!isAuth || !id) return;
    notesApi
      .list(Number(id), { limit: 100 })
      .then((res) => {
        setApiNotes(
          res.data.map((n) => ({
            id: String(n.id),
            text: n.text,
            date: n.createdAt?.split('T')[0] || new Date().toISOString().split('T')[0],
          })),
        );
      })
      .catch(() => {});
  }, [isAuth, id]);

  // Guest mode: handle legacy string notes to prevent crashes
  let guestNotes: { id: string; text: string; date: string }[] = [];
  if (!isAuth) {
    if (Array.isArray(contact?.notes)) {
      guestNotes = contact.notes;
    } else if (typeof contact?.notes === 'string') {
      guestNotes = [
        {
          id: 'legacy-note',
          text: contact?.notes,
          date: contact?.lastInteractionDate || new Date().toISOString().split('T')[0],
        },
      ];
    }
  }

  const normalizedComments = isAuth ? apiNotes : guestNotes;

  const timelineItems = [
    ...interactions.map((i) => ({ type: 'interaction' as const, date: i.date, data: i })),
    ...normalizedComments.map((c) => ({ type: 'note' as const, date: c.date, data: c })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const [commentText, setCommentText] = useState('');
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [phoneActionMethod, setPhoneActionMethod] = useState<CommunicationMethod | null>(null);

  // ─── Save note (API for auth, local for guest) ───────────────────────────
  const handleSaveComment = useCallback(() => {
    if (!commentText.trim() || !contact) return;
    const text = commentText.trim();
    setCommentText('');

    if (isAuth) {
      const tempId = 'tmp-' + Math.random().toString(36).substr(2, 9);
      const tempNote = { id: tempId, text, date: new Date().toISOString().split('T')[0] };
      setApiNotes((prev) => [tempNote, ...prev]);
      setSavingNoteIds((prev) => new Set(prev).add(tempId));

      notesApi
        .create(Number(contact.id), text)
        .then((saved) => {
          // Replace temp with real API note
          setApiNotes((prev) =>
            prev.map((n) =>
              n.id === tempId
                ? {
                    id: String(saved.id),
                    text: saved.text,
                    date: saved.createdAt?.split('T')[0] || tempNote.date,
                  }
                : n,
            ),
          );
          setSavingNoteIds((prev) => {
            const next = new Set(prev);
            next.delete(tempId);
            return next;
          });
        })
        .catch(() => {
          setSavingNoteIds((prev) => {
            const next = new Set(prev);
            next.delete(tempId);
            return next;
          });
          setFailedNoteIds((prev) => new Set(prev).add(tempId));
        });
    } else {
      const newComment = {
        id: Math.random().toString(36).substr(2, 9),
        text,
        date: new Date().toISOString().split('T')[0],
      };
      updateContact(contact.id, { notes: [newComment, ...guestNotes] });
    }
  }, [commentText, contact, isAuth, guestNotes, updateContact]);

  // ─── Retry failed note ────────────────────────────────────────────────────
  const handleRetryNote = useCallback(
    (noteId: string) => {
      if (!contact) return;
      const note = apiNotes.find((n) => n.id === noteId);
      if (!note) return;

      setFailedNoteIds((prev) => {
        const next = new Set(prev);
        next.delete(noteId);
        return next;
      });
      setSavingNoteIds((prev) => new Set(prev).add(noteId));

      notesApi
        .create(Number(contact.id), note.text)
        .then((saved) => {
          setApiNotes((prev) =>
            prev.map((n) =>
              n.id === noteId
                ? {
                    id: String(saved.id),
                    text: saved.text,
                    date: saved.createdAt?.split('T')[0] || note.date,
                  }
                : n,
            ),
          );
          setSavingNoteIds((prev) => {
            const next = new Set(prev);
            next.delete(noteId);
            return next;
          });
        })
        .catch(() => {
          setSavingNoteIds((prev) => {
            const next = new Set(prev);
            next.delete(noteId);
            return next;
          });
          setFailedNoteIds((prev) => new Set(prev).add(noteId));
        });
    },
    [contact, apiNotes],
  );

  // ─── Remove note ──────────────────────────────────────────────────────────
  const handleRemoveComment = useCallback(
    (commentId: string) => {
      if (!contact) return;
      setItemToDelete(null);

      if (isAuth) {
        // If it's a failed temp note, just remove locally
        if (failedNoteIds.has(commentId) || commentId.startsWith('tmp-')) {
          setApiNotes((prev) => prev.filter((n) => n.id !== commentId));
          setFailedNoteIds((prev) => {
            const next = new Set(prev);
            next.delete(commentId);
            return next;
          });
          return;
        }
        // Optimistic remove
        const removed = apiNotes.find((n) => n.id === commentId);
        setApiNotes((prev) => prev.filter((n) => n.id !== commentId));
        notesApi.delete(Number(contact.id), Number(commentId)).catch(() => {
          // Restore on failure
          if (removed) setApiNotes((prev) => [removed, ...prev]);
        });
      } else {
        updateContact(contact.id, { notes: guestNotes.filter((c) => c.id !== commentId) });
      }
    },
    [contact, isAuth, apiNotes, failedNoteIds, guestNotes, updateContact],
  );

  const handleDeleteInteraction = (interactionId: string) => {
    removeInteraction(interactionId);
    setItemToDelete(null);
  };

  if (!contact) return <div className="p-8 text-center">Contact not found</div>;

  // Determine top 2 communication methods to display (preferred first)
  let displayMethods: CommunicationMethod[] = [];
  if (contact.communicationMethods && contact.communicationMethods.length > 0) {
    if (contact.preferredCommMethod) {
      const preferred = contact.communicationMethods.find((m) =>
        isPreferred(contact.preferredCommMethod, m),
      );
      if (preferred) displayMethods.push(preferred);
    }
    const others = contact.communicationMethods.filter(
      (m) => !isPreferred(contact.preferredCommMethod, m),
    );
    displayMethods = [...displayMethods, ...others].slice(0, 2);
  }

  return (
    <div className="bg-[#f8f5f5] min-h-screen flex flex-col">
      {/* Header */}
      <header className="flex items-center p-4 justify-between sticky top-0 bg-[#f8f5f5]/80 backdrop-blur-md z-10">
        <button
          onClick={() => navigate(-1)}
          className="size-10 rounded-full hover:bg-slate-200/50 flex items-center justify-center transition-colors"
        >
          <ArrowLeft size={24} className="text-slate-600" />
        </button>
        <h1 className="text-lg font-bold">Contact Details</h1>
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="size-10 flex items-center justify-center rounded-full hover:bg-[#fa8585]/10 text-slate-900"
          >
            <MoreVertical size={24} />
          </button>
          {showMenu && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-12 bg-white rounded-2xl shadow-xl border border-slate-100 py-2 z-30 min-w-[160px] animate-in fade-in slide-in-from-top-2 duration-200">
                <button
                  onClick={() => {
                    setShowMenu(false);
                    navigate(`/edit-contact/${contact.id}`);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                >
                  <Pencil size={18} className="text-slate-600" />
                  <span className="text-sm font-semibold text-slate-700">Edit Contact</span>
                </button>
                <div className="h-px bg-slate-100 my-1 mx-4"></div>
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setItemToDelete({ id: contact.id, type: 'contact' });
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 transition-colors group"
                >
                  <Trash2 size={18} className="text-red-400 group-hover:text-red-500" />
                  <span className="text-sm font-semibold text-red-500 whitespace-nowrap">
                    Delete Contact
                  </span>
                </button>
              </div>
            </>
          )}
        </div>
      </header>

      {/* Profile Section */}
      <div className="flex flex-col items-center p-6 gap-4">
        <div className="relative">
          <div className="size-36 rounded-full border-4 border-[#fa8585] p-1">
            <ContactAvatar
              photoUrl={contact.photoUrl}
              name={contact.name}
              size="xl"
              className="size-full"
            />
          </div>
          <div className="absolute bottom-0 right-0 bg-[#fa8585] text-white size-10 rounded-full flex items-center justify-center border-4 border-white">
            <span className="text-xs font-bold">
              {contact.healthScore >= 0 ? <AnimatedNumber value={contact.healthScore} /> : 'N/A'}
            </span>
          </div>
        </div>
        <div className="text-center">
          <h2 className="text-2xl font-bold">{contact.name}</h2>
          <p className="text-slate-500 font-medium">
            {contact.relationshipType} {contact.innerCircle && '• Inner Circle'}
          </p>
        </div>
      </div>

      {/* Urgent Alert */}
      {contact.status === 'overdue' && (
        <div className="px-4 mb-6">
          <div className="bg-[#fa8585]/10 border border-[#fa8585]/20 rounded-xl p-4 flex items-center gap-4">
            <div className="bg-[#fa8585] text-white size-12 rounded-full flex items-center justify-center shrink-0">
              <AlertTriangle size={24} />
            </div>
            <div className="flex-1">
              <p className="font-bold text-slate-900">Urgent: Reconnect Now</p>
              <p className="text-sm text-slate-600">
                {contact.overdueDays} days since last contact. Reach out today.
              </p>
            </div>
            <ArrowLeft className="rotate-180 text-[#fa8585]" size={20} />
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="grid grid-cols-3 gap-3 px-4 mb-8">
        {displayMethods.length > 0 ? (
          displayMethods.map((method, idx) =>
            method.type.toLowerCase() === 'phone' ? (
              <button
                key={`${method.type}-${idx}`}
                onClick={() => setPhoneActionMethod(method)}
                className={`flex flex-col items-center justify-center gap-2 p-4 text-white rounded-xl shadow-lg active:scale-95 transition-transform ${idx === 0 ? 'bg-[#fa8585] shadow-[#fa8585]/20' : 'bg-slate-800 shadow-slate-900/10'}`}
              >
                {getCommIcon(method.type, 24)}
                <span className="text-xs font-bold uppercase truncate w-full text-center px-1">
                  {method.type}
                </span>
              </button>
            ) : (
              <a
                key={`${method.type}-${idx}`}
                href={getCommHref(method)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  setPendingInteraction({
                    contactId: contact.id,
                    type: 'text',
                  })
                }
                className={`flex flex-col items-center justify-center gap-2 p-4 text-white rounded-xl shadow-lg active:scale-95 transition-transform ${idx === 0 ? 'bg-[#fa8585] shadow-[#fa8585]/20' : 'bg-slate-800 shadow-slate-900/10'}`}
              >
                {getCommIcon(method.type, 24)}
                <span className="text-xs font-bold uppercase truncate w-full text-center px-1">
                  {method.type}
                </span>
              </a>
            ),
          )
        ) : (
          <button
            onClick={() => navigate(`/edit-contact/${contact.id}`)}
            className="col-span-2 flex flex-col items-center justify-center gap-2 p-4 bg-[#fa8585] text-white rounded-xl shadow-lg shadow-[#fa8585]/20 active:scale-95 transition-transform"
          >
            <Plus size={24} />
            <span className="text-xs font-bold uppercase">Add Contact Info</span>
          </button>
        )}
        <button
          onClick={() => navigate('/log', { state: { contactId: contact.id } })}
          className={`flex flex-col items-center justify-center gap-2 p-4 bg-slate-100 text-slate-900 rounded-xl active:scale-95 transition-transform ${displayMethods.length === 0 ? 'col-span-1' : ''}`}
        >
          <Edit3 size={24} />
          <span className="text-xs font-bold uppercase">Log Interaction</span>
        </button>
      </div>

      {/* Details Section */}
      <div className="px-4 space-y-4 mb-8">
        <h3 className="font-bold text-slate-500 text-sm uppercase tracking-wider">Details</h3>
        <div className="bg-slate-50 rounded-xl p-4 space-y-4">
          {contact.birthday && (
            <div className="flex items-center gap-4">
              <Cake className="text-[#fa8585]" size={20} />
              <div>
                <p className="text-xs text-slate-500">Birthday</p>
                <p className="font-medium">{formatDate(contact.birthday)}</p>
              </div>
            </div>
          )}
          {contact.lastInteractionDate && (
            <div className="flex items-center gap-4">
              <History className="text-[#fa8585]" size={20} />
              <div>
                <p className="text-xs text-slate-500">Last Interaction</p>
                <p className="font-medium">{formatDate(contact.lastInteractionDate)}</p>
              </div>
            </div>
          )}
          {contact.contactFrequencyDays && (
            <div className="flex items-center gap-4">
              <RefreshCw className="text-[#fa8585]" size={20} />
              <div>
                <p className="text-xs text-slate-500">Reach Out Every</p>
                <p className="font-medium">
                  {contact.contactFrequencyDays === 1
                    ? 'Every day'
                    : contact.contactFrequencyDays === 7
                      ? 'Every week'
                      : contact.contactFrequencyDays === 14
                        ? 'Every 2 weeks'
                        : contact.contactFrequencyDays === 30
                          ? 'Every month'
                          : `Every ${contact.contactFrequencyDays} days`}
                </p>
              </div>
            </div>
          )}
          {contact.tags && contact.tags.length > 0 && (
            <div className="flex items-center gap-4">
              <Tag className="text-[#fa8585]" size={20} />
              <div className="flex flex-wrap gap-2">
                {contact.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 bg-[#fa8585]/10 text-[#fa8585] text-xs font-bold rounded"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {contact.communicationMethods && contact.communicationMethods.length > 0 && (
            <div className="pt-2 border-t border-slate-200/60 space-y-4">
              {contact.communicationMethods.map((method, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <div className="text-[#fa8585]">{getCommIcon(method.type, 20)}</div>
                  <div>
                    <p className="text-xs text-slate-500 capitalize">{method.type}</p>
                    {method.type.toLowerCase() === 'phone' ? (
                      <button
                        onClick={() => setPhoneActionMethod(method)}
                        className="font-medium text-slate-900 hover:text-blue-500 transition-colors"
                      >
                        {method.value}
                      </button>
                    ) : (
                      <a
                        href={getCommHref(method)}
                        onClick={() =>
                          setPendingInteraction({
                            contactId: contact.id,
                            type: 'text',
                          })
                        }
                        className="font-medium inline-block text-slate-900 hover:text-blue-500 transition-colors"
                      >
                        {method.value}
                      </a>
                    )}
                  </div>
                  {isPreferred(contact.preferredCommMethod, method) && (
                    <div className="ml-auto">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-600 uppercase tracking-widest">
                        Preferred
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Note Section */}
      <div className="px-4 pb-8">
        <h3 className="font-bold text-slate-500 text-sm uppercase tracking-wider mb-4">Notes</h3>
        <textarea
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder="Any additional thoughts or details about this person..."
          className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:ring-2 focus:ring-[#fa8585] focus:border-[#fa8585] p-4 text-slate-900 placeholder-slate-400 outline-none transition-all resize-none mb-3"
          rows={3}
        />
        <div className="flex justify-end">
          <button
            onClick={handleSaveComment}
            disabled={!commentText.trim()}
            className="px-6 py-2 rounded-full text-sm font-bold bg-[#fa8585] text-white disabled:opacity-50 transition-all active:scale-95 shadow-sm shadow-[#fa8585]/20 uppercase tracking-wide"
          >
            Add
          </button>
        </div>
      </div>

      {/* History Section */}
      <div className="px-4 pb-12">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-500 text-sm uppercase tracking-wider">
            Relationship History
          </h3>
          <button
            onClick={() => navigate(`/contact/${contact.id}/history`)}
            className="text-[#fa8585] text-sm font-bold active:scale-95 transition-transform"
          >
            View All
          </button>
        </div>
        <div className="space-y-6 relative before:absolute before:left-4.75 before:top-2 before:bottom-0 before:w-0.5 before:bg-slate-200">
          {timelineItems.map((item) => {
            if (item.type === 'note') {
              const note = item.data as any;
              const isFailed = failedNoteIds.has(note.id);
              const isSaving = savingNoteIds.has(note.id);
              return (
                <div key={`note-${note.id}`} className="relative pl-12 pb-6">
                  <div
                    className={cn(
                      'absolute left-0 size-10 rounded-full border-2 border-white flex items-center justify-center z-10',
                      isFailed ? 'bg-amber-50 text-amber-500' : 'bg-blue-50 text-blue-500',
                    )}
                  >
                    {isFailed ? <AlertTriangle size={18} /> : <StickyNote size={18} />}
                  </div>
                  <div className="pt-1">
                    <div className="flex justify-between items-center mb-1">
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                          Note
                        </p>
                        {isFailed && (
                          <span className="text-[10px] font-bold text-amber-500">Not synced</span>
                        )}
                        {isSaving && (
                          <span className="text-[10px] font-medium text-slate-300">Saving...</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {isFailed && (
                          <button
                            onClick={() => handleRetryNote(note.id)}
                            className="text-amber-500 hover:text-amber-600 transition-all p-1"
                            title="Retry sync"
                          >
                            <RefreshCw size={14} />
                          </button>
                        )}
                        <span className="text-xs font-medium text-slate-400">
                          {formatDate(note.date)}
                        </span>
                        <button
                          onClick={() => setItemToDelete({ id: note.id, type: 'note' })}
                          className="text-slate-300 hover:text-red-500 transition-all p-1"
                          title="Delete note"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.text}</p>
                  </div>
                </div>
              );
            } else {
              const interaction = item.data as any;
              return (
                <div key={`int-${interaction.id}`} className="relative pl-12 pb-6">
                  <div
                    className={cn(
                      'absolute left-0 size-10 rounded-full flex items-center justify-center z-10',
                      'bg-slate-50 text-slate-400 border-2 border-white',
                    )}
                  >
                    {interaction.type === 'call' ? (
                      <Phone size={18} />
                    ) : interaction.type === 'text' ? (
                      <Mail size={18} />
                    ) : interaction.type === 'in_person' ? (
                      <Smile size={18} />
                    ) : interaction.type === 'video' ? (
                      <User size={18} />
                    ) : (
                      <History size={18} />
                    )}
                  </div>
                  <div className="pt-1">
                    <div className="flex justify-between items-start mb-1">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                        Interaction
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-slate-400">
                          {formatDate(interaction.date)}
                        </span>
                        <button
                          onClick={() =>
                            setItemToDelete({ id: interaction.id, type: 'interaction' })
                          }
                          className="text-slate-300 hover:text-red-500 transition-all p-1"
                          title="Delete interaction"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mb-1">
                      <span className="px-2 py-0.5 rounded-full bg-slate-100/50 border border-slate-200 text-[10px] font-medium text-slate-500 capitalize whitespace-nowrap">
                        {formatInteractionType(interaction.type)}
                      </span>
                      {interaction.rating && (
                        <span
                          className={cn(
                            'px-2 py-0.5 rounded-full border text-[10px] font-medium capitalize whitespace-nowrap flex items-center gap-1',
                            interaction.rating === 'good'
                              ? 'bg-green-50 text-green-600 border-green-200'
                              : interaction.rating === 'ok'
                                ? 'bg-yellow-50 text-yellow-600 border-yellow-200'
                                : 'bg-red-50 text-red-600 border-red-200',
                          )}
                        >
                          {interaction.rating === 'good' && <Smile size={10} />}
                          {interaction.rating === 'ok' && <Meh size={10} />}
                          {interaction.rating === 'bad' && <Frown size={10} />}
                          {interaction.rating}
                        </span>
                      )}
                    </div>
                    <p
                      className={cn(
                        'text-sm mt-1',
                        interaction.notes ? 'text-slate-700' : 'text-slate-300 italic',
                      )}
                    >
                      {interaction.notes || 'No notes'}
                    </p>
                    {interaction.attachments && interaction.attachments.length > 0 && (
                      <div className="flex gap-2 mt-2">
                        {interaction.attachments.map((url: string, idx: number) => (
                          <button
                            key={idx}
                            onClick={() => setLightboxUrl(url)}
                            className="h-20 max-w-32 rounded-lg overflow-hidden border border-slate-200"
                          >
                            <img src={url} alt="" className="h-full w-auto object-contain" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            }
          })}
        </div>
        {/* Year Met — always shown as the origin, outside timeline line */}
        <div className="relative pl-12 mt-6">
          {timelineItems.length > 0 && (
            <div className="absolute left-4.75 -top-6 w-0.5 h-6 bg-slate-200" />
          )}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 size-10 rounded-full bg-orange-50 text-orange-500 border-2 border-white flex items-center justify-center z-10 text-xl leading-none">
            <Egg size={20} />
          </div>
          <div>
            <p className="font-bold text-sm">Met {contact.name.split(' ')[0]}</p>
            <p className="text-xs text-slate-500">{contact.yearMet || 'Once upon a time'}</p>
          </div>
        </div>
      </div>
      {/* Phone Action Sheet */}
      {phoneActionMethod && (
        <div
          className="fixed inset-0 z-60 flex items-end justify-center bg-slate-900/60 backdrop-blur-sm"
          onClick={() => setPhoneActionMethod(null)}
        >
          <div
            className="bg-white rounded-t-4xl w-full max-w-lg p-6 pb-8 animate-in slide-in-from-bottom duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-5" />
            <p className="text-sm text-slate-500 text-center mb-4">{phoneActionMethod.value}</p>
            <div className="grid grid-cols-2 gap-3">
              <a
                href={getCommHref(phoneActionMethod, 'call')}
                onClick={() => {
                  setPendingInteraction({ contactId: contact.id, type: 'call' });
                  setPhoneActionMethod(null);
                }}
                className="flex flex-col items-center gap-2 p-4 bg-[#fa8585] text-white rounded-xl shadow-lg shadow-[#fa8585]/20 active:scale-95 transition-transform"
              >
                <Phone size={24} />
                <span className="text-sm font-bold">Call</span>
              </a>
              <a
                href={getCommHref(phoneActionMethod, 'sms')}
                onClick={() => {
                  setPendingInteraction({ contactId: contact.id, type: 'text' });
                  setPhoneActionMethod(null);
                }}
                className="flex flex-col items-center gap-2 p-4 bg-slate-800 text-white rounded-xl shadow-lg shadow-slate-900/10 active:scale-95 transition-transform"
              >
                <MessageCircle size={24} />
                <span className="text-sm font-bold">Text</span>
              </a>
            </div>
            <button
              onClick={() => setPhoneActionMethod(null)}
              className="w-full mt-3 py-3 rounded-xl text-sm font-bold text-slate-500 bg-slate-100 active:scale-95 transition-transform"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {itemToDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-[2rem] p-6 w-full max-w-sm shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3 mb-4 text-[#fa8585]">
              <AlertTriangle size={24} />
              <h3 className="font-bold text-lg">
                {itemToDelete.type === 'contact' ? 'Delete Contact?' : 'Delete item?'}
              </h3>
            </div>
            <p className="text-slate-600 mb-6">
              {itemToDelete.type === 'contact'
                ? `Are you sure you want to delete ${contact.name}? This will remove all their history and cannot be undone.`
                : 'Are you sure you want to remove this from your relationship history? This action cannot be undone.'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setItemToDelete(null)}
                className="flex-1 py-3 px-4 rounded-xl font-bold bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (itemToDelete.type === 'note') {
                    handleRemoveComment(itemToDelete.id);
                  } else if (itemToDelete.type === 'interaction') {
                    handleDeleteInteraction(itemToDelete.id);
                  } else if (itemToDelete.type === 'contact') {
                    deleteContact(contact.id);
                    navigate('/', { replace: true });
                  }
                }}
                className="flex-1 py-3 px-4 rounded-xl font-bold bg-red-500 text-white hover:bg-red-600 transition-colors shadow-lg shadow-red-500/20"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxUrl && <ImageLightbox src={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
    </div>
  );
}
